import { FC, memo, useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { Button, Input } from 'antd';
import Link from 'next/link';
import { ConfigProvider } from 'antd';
import Icon from '@ant-design/icons';
import styles from './PromotionQuote.module.scss';
import { PromotionQuoteProps } from './PromotionQuote.types';
import DiscountIcon from '@/public/icon/discount-no-shadow.svg';
import ErrorIcon from '@/public/icon/icon-error.svg';
import classNames from 'classnames';

const PromotionQuote = memo((props: PromotionQuoteProps) => {
  const {
    promotionQuote,
    onPromotionCodeChange,
    defaultPromotion,
    promotionCode,
    errorQuote,
    isDifferencePromotion = false,
    loading = false
  } = props;
  const [isError, setIsError] = useState<Boolean>(false);
  const [promoCode, setPromoCode] = useState<string>('');
  const [promoState, setPromoState] = useState<any>('normal');
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleApplyPromotion = useCallback(() => {
    setIsLoading(true);
    const isChangeVal = promoCode !== promotionQuote?.frontendCode;
    if (isChangeVal || promoCode === '') {
      onPromotionCodeChange && onPromotionCodeChange(promoCode);
    }
  }, [promoCode, promotionQuote, onPromotionCodeChange]);

  const handleCancelEdit = useCallback(() => {
    // if promo code is in error status,change promo state as add and clear the promocode
    setPromoState('normal');
    if (errorQuote === 'error') {
      setPromoCode('');
      onPromotionCodeChange && onPromotionCodeChange('');
    } else {
      setPromoCode(promotionCode);
    }
    setIsError(false);
  }, [errorQuote, onPromotionCodeChange, promotionCode]);

  const handleEditPromo = useCallback(() => {
    if (!promotionCode || errorQuote === 'no-error') {
      setPromoCode('');
    }
    setPromoState(!promotionCode || errorQuote === 'error' ? 'add' : 'edit');
  }, [promotionCode, errorQuote]);

  useEffect(() => {
    if (errorQuote === 'no-error') {
      setPromoState('normal');
      setIsError(false);
      setIsLoading(false);
    }
    if (errorQuote === 'error') {
      setIsError(true);
      setIsLoading(false);
    }
  }, [errorQuote]);

  useEffect(() => {
    if (errorQuote === 'no-error' && promotionCode) {
      setPromoCode(promotionCode);
    }
    if (defaultPromotion === promotionCode && errorQuote === 'error') {
      setIsError(true);
      setPromoCode(defaultPromotion);
      setPromoState('edit');
    }
  }, [defaultPromotion, errorQuote, promoState, promotionCode]);

  useEffect(() => {
    if (promotionQuote?.frontendCode) {
      setPromoCode(promotionQuote?.frontendCode);
    }
  }, [promotionQuote]);

  return (
    <>
      {promoState === 'normal' && (
        <div className={styles.promotion_quote_container}>
          <div className={styles.promotion_quote_icon_content}>
            <Icon component={DiscountIcon} className={styles.discount_icon} />
            <div
              className={classNames(
                errorQuote !== 'error' && promotionCode
                  ? styles.promotion_content_container
                  : styles.promotion_content_container_without_promotion
              )}
            >
              {!isDifferencePromotion && (
                <div className={styles.promo_text}>
                  {promotionQuote?.descZh ? `${promotionQuote?.descZh}` : ''}
                </div>
              )}
              <div
                className={classNames(
                  isDifferencePromotion ? styles.promo_code_big_size : styles.promo_code
                )}
              >
                {promoCode && !isError ? `已使用優惠碼 ${promoCode}。` : '使用優惠碼'}
              </div>
            </div>
          </div>

          <div className={styles.promotion_operation} onClick={handleEditPromo}>
            {!promoCode || isError ? '添加' : '編輯'}
          </div>
        </div>
      )}
      {(promoState === 'edit' || promoState === 'add') && (
        <div className={styles.promotion_quote_edit_container}>
          <div className={styles.promotion_edit_header}>
            <div className={styles.promotion_title}>
              <Icon component={DiscountIcon} className={styles.discount_icon} />
              <span className={styles.discount_title}>
                {promoState === 'edit' ? '更改優惠碼' : '使用優惠碼'}
              </span>
            </div>

            <span className={styles.promotion_cancel_btn} onClick={handleCancelEdit}>
              取消
            </span>
          </div>
          <ConfigProvider
            theme={{
              components: {
                Input: {
                  paddingInline: 16,
                  paddingBlock: 11
                }
              }
            }}
          >
            <Input
              status={isError && 'error'}
              placeholder={promoState === 'edit' ? '更改優惠碼' : '使用優惠碼'}
              style={{ fontFamily: 'FWDCircularTT-Book, sans-serif !important' }}
              value={promoCode}
              onChange={e => setPromoCode(e.target.value)}
              suffix={
                <ConfigProvider
                  theme={{
                    components: {
                      Button: {
                        fontWeight: 700,
                        contentFontSize: 14,
                        contentLineHeight: 18,
                        paddingBlock: 10
                      }
                    }
                  }}
                >
                  <Button
                    className={styles.promotion_input_suffix}
                    loading={isLoading}
                    onClick={handleApplyPromotion}
                    type="link"
                  >
                    {!isLoading ? '申請' : ''}
                  </Button>
                </ConfigProvider>
              }
              className={styles.promotion_input}
            />
          </ConfigProvider>
          {isError && (
            <div className={styles.promotion_error_content}>
              <Icon component={ErrorIcon} className={styles.error_icon} />
              <span className={styles.error_text}>優惠碼無效或名額已滿</span>
            </div>
          )}
        </div>
      )}
    </>
  );
});
export default PromotionQuote;
